import { useCallback, useEffect, useRef, useState } from "react";

// Tracks whether `data` has changed from a captured baseline and guards a
// close action behind a confirmation when there are unsaved changes.
//
// Usage:
//   const guard = useUnsavedChanges(data, closeModal);
//   guard.setBaseline(data)        // call once real initial data is loaded
//   onHide={guard.requestClose}    // show confirm if dirty, else close
//   ...render <ConfirmationModal show={guard.showConfirm} ... />
const useUnsavedChanges = (data: any, onClose: () => void) => {
  const baselineRef = useRef<string>(JSON.stringify(data ?? null));
  const [showConfirm, setShowConfirm] = useState(false);

  // capture/replace the baseline (e.g. after async load or a successful save)
  const setBaseline = useCallback((value: any) => {
    baselineRef.current = JSON.stringify(value ?? null);
  }, []);

  const isDirty = useCallback(
    () => JSON.stringify(data ?? null) !== baselineRef.current,
    [data]
  );

  // guarded close: confirm if dirty, otherwise close immediately
  const requestClose = useCallback(() => {
    if (isDirty()) {
      setShowConfirm(true);
    } else {
      onClose();
    }
  }, [isDirty, onClose]);

  const confirmDiscard = useCallback(() => {
    setShowConfirm(false);
    onClose();
  }, [onClose]);

  const cancelClose = useCallback(() => setShowConfirm(false), []);

  // warn on browser tab close / refresh while there are unsaved changes
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (isDirty()) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isDirty]);

  return {
    showConfirm,
    requestClose,
    confirmDiscard,
    cancelClose,
    setBaseline,
    isDirty,
  };
};

export default useUnsavedChanges;
